import { EmailOutlined, LocalPhoneOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

import CancelButton from "../provider-management/components/buttons/CancelButton";
import EditButton from "../provider-management/components/buttons/EditButton";
import SaveButton from "../provider-management/components/buttons/SaveButton";

import useProviderDetailAdditionalContactSection from "./hooks/useProviderDetailAdditionalContactSection";
import {
  getEmailTypeKey,
  getEmailValue,
  getOptionLabelByKey,
  getPhoneDisplayLabel,
  getPhoneValue,
} from "./sections/additional-contact/additionalContactUtils";
import ContactPanel from "./sections/additional-contact/ContactPanel";
import ProviderDetailSectionCard from "./sections/shared/ProviderDetailSectionCard";
import ProviderDetailSectionTitle from "./sections/shared/ProviderDetailSectionTitle";
import { PROVIDER_DETAIL_STYLES } from "./styles";

function getAddTooltip({ isEditing, canAdd, disabled, type }) {
  if (!isEditing) {return `Enter edit mode to add ${type}`;}
  if (!canAdd) {return `You don't have permission to add ${type}`;}
  return disabled ? `No additional ${type} types available` : `Add ${type}`;
}

export default function ProviderDetailAdditionalContactSection(props) {
  const section = useProviderDetailAdditionalContactSection(props);
  const { permissionState } = props;
  if (!section.canRender) {return null;}

  return (
    <ProviderDetailSectionCard>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <ProviderDetailSectionTitle>Additional Contact Information</ProviderDetailSectionTitle>
          <Tooltip title="Edit">
            <span>
              <EditButton
                disabled={!permissionState.canEditAdditionalContacts || section.isSectionEditing}
                handleOnClick={!section.isSectionEditing ? section.enterEditMode : undefined}
              />
            </span>
          </Tooltip>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {permissionState.canViewEmails ? (
            <ContactPanel
              addButtonId="provider-detail-add-email-button"
              addTooltip={getAddTooltip({ isEditing: section.editingState.emails, canAdd: permissionState.canAddEmails, disabled: section.emailAddDisabled, type: "emails" })}
              canAdd={permissionState.canAddEmails}
              canDeleteRows={permissionState.canDeleteEmails}
              canEditRows={permissionState.canEditEmails}
              disableAdd={section.emailAddDisabled}
              displayItems={section.emails}
              draftRows={section.emailDraftRows}
              emptyText="No additional emails added"
              getDisplayLabel={(item) => getOptionLabelByKey(section.emailTypeOptions, getEmailTypeKey(item)) || "Email"}
              getDisplayValue={getEmailValue}
              getRowOptions={section.getEmailRowOptions}
              headerIcon={<EmailOutlined sx={PROVIDER_DETAIL_STYLES.contactPanelHeaderIcon} />}
              inputIdPrefix="provider-detail-email-input"
              isEditing={section.isSectionEditing && (permissionState.canEditEmails || permissionState.canAddEmails || permissionState.canDeleteEmails)}
              isLoading={section.loadingState.emails}
              onAdd={section.addEmailRow}
              onRemoveDraft={section.removeEmailRow}
              onRowChange={section.updateEmailRow}
              panelTitle="Emails"
              selectIdPrefix="provider-detail-email-type-select"
              valueLabel="Email Address"
            />
          ) : null}

          {permissionState.canViewPhones ? (
            <ContactPanel
              addButtonId="provider-detail-add-phone-button"
              addTooltip={getAddTooltip({ isEditing: section.editingState.phones, canAdd: permissionState.canAddPhones, disabled: section.phoneAddDisabled, type: "phone" })}
              canAdd={permissionState.canAddPhones}
              canDeleteRows={permissionState.canDeletePhones}
              canEditRows={permissionState.canEditPhones}
              disableAdd={section.phoneAddDisabled}
              displayItems={section.phones}
              draftRows={section.phoneDraftRows}
              emptyText="No additional phones added"
              getDisplayLabel={(item) => getPhoneDisplayLabel(item, section.phoneTypeOptions)}
              getDisplayValue={getPhoneValue}
              getRowOptions={section.getPhoneRowOptions}
              headerIcon={<LocalPhoneOutlined sx={PROVIDER_DETAIL_STYLES.contactPanelHeaderIcon} />}
              inputIdPrefix="provider-detail-phone-input"
              isEditing={section.isSectionEditing && (permissionState.canEditPhones || permissionState.canAddPhones || permissionState.canDeletePhones)}
              isLoading={section.loadingState.phones}
              onAdd={section.addPhoneRow}
              onRemoveDraft={section.removePhoneRow}
              onRowChange={section.updatePhoneRow}
              panelTitle="Phones"
              selectIdPrefix="provider-detail-phone-type-select"
              valueLabel="Phones"
            />
          ) : null}
        </div>

        {section.isSectionEditing ? (
          <div className="flex justify-end pt-2">
            <div className="flex w-full gap-2 md:w-1/2 md:justify-end">
              <CancelButton disabled={section.loadingState.saving} onClick={section.cancelSection} />
              <SaveButton disabled={section.saveDisabled} onClick={section.saveSection} label="Save" />
            </div>
          </div>
        ) : null}
      </div>
    </ProviderDetailSectionCard>
  );
}

ProviderDetailAdditionalContactSection.propTypes = {
  permissionState: PropTypes.object.isRequired,
  providerInformation: PropTypes.object.isRequired,
  setProviderInformation: PropTypes.func,
};

ProviderDetailAdditionalContactSection.defaultProps = {
  setProviderInformation: null,
};
